import { supabase } from "./supabase";

const IMAGE_BUCKET = "menu-images";

export type Category = {
  id: number;
  name: string;
};

export type PublicMenuItem = {
  id: number;
  image?: string;
  title: string;
  description?: string;
  price: number;
  extras?: string[];
};

export type AdminMenuItem = {
  id: number;
  name: string;
  price: number;
  description?: string;
  extras?: string[];
  image_url?: string;
  image_path?: string;
};

type MenuItemRow = {
  id: number;
  name: string | null;
  description: string | null;
  price: number | string | null;
  image_url: string | null;
  image_path: string | null;
  extras: string[] | null;
};

const toPrice = (value: number | string | null | undefined) => {
  if (typeof value === "number") return value;
  return parseFloat(String(value ?? 0)) || 0;
};

export async function getCategories(): Promise<Category[]> {
  const { data, error } = await supabase
    .from("categories")
    .select("id, name")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: Number(row.id),
    name: row.name ?? "Kategori",
  }));
}

export async function getPublicMenuItems(categoryId: number): Promise<PublicMenuItem[]> {
  const { data, error } = await supabase
    .from("menu_items")
    .select("id, name, description, price, image_url, image_path, extras")
    .eq("category_id", categoryId)
    .eq("is_available", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as MenuItemRow[]).map((row) => ({
    id: row.id,
    image: row.image_url ?? undefined,
    title: row.name ?? "Ürün",
    description: row.description ?? undefined,
    price: toPrice(row.price),
    extras: Array.isArray(row.extras) && row.extras.length ? row.extras : undefined,
  }));
}

export async function getAdminMenuItems(categoryId: number): Promise<AdminMenuItem[]> {
  const { data, error } = await supabase
    .from("menu_items")
    .select("id, name, description, price, extras, image_url, image_path")
    .eq("category_id", categoryId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as MenuItemRow[]).map((row) => ({
    id: row.id,
    name: row.name ?? "Ürün",
    price: toPrice(row.price),
    description: row.description ?? undefined,
    extras: Array.isArray(row.extras) ? row.extras : [],
    image_url: row.image_url ?? undefined,
    image_path: row.image_path ?? undefined,
  }));
}

export type ImageCropFocus = {
  x: number;
  y: number;
};

export async function createMenuItem(input: {
  categoryId: number;
  name: string;
  price: number;
  description?: string;
  extras?: string[];
  imageUrl?: string;
  imagePath?: string;
}): Promise<AdminMenuItem> {
  const { data, error } = await supabase
    .from("menu_items")
    .insert({
      category_id: input.categoryId,
      name: input.name,
      price: input.price,
      description: input.description || null,
      extras: input.extras ?? [],
      image_url: input.imageUrl || null,
      image_path: input.imagePath || null,
    })
    .select("id, name, description, price, extras, image_url, image_path")
    .single();

  if (error) throw error;

  return {
    id: data.id,
    name: data.name,
    price: toPrice(data.price),
    description: data.description ?? undefined,
    extras: Array.isArray(data.extras) ? data.extras : [],
    image_url: data.image_url ?? undefined,
    image_path: data.image_path ?? undefined,
  };
}

export async function updateMenuItem(
  id: number,
  updates: {
    price: number;
    description: string;
    extras?: string[];
    imageUrl?: string;
    imagePath?: string;
  }
): Promise<void> {
  const payload: {
    price: number;
    description: string;
    extras: string[];
    image_url?: string;
    image_path?: string;
  } = {
    price: updates.price,
    description: updates.description,
    extras: updates.extras ?? [],
  };

  if (updates.imageUrl && updates.imagePath) {
    payload.image_url = updates.imageUrl;
    payload.image_path = updates.imagePath;
  }

  const { error } = await supabase
    .from("menu_items")
    .update(payload)
    .eq("id", id);

  if (error) throw error;
}

export async function deleteMenuItem(id: number): Promise<void> {
  const { error } = await supabase.from("menu_items").delete().eq("id", id);
  if (error) throw error;
}

async function resizeImage(file: File, focus?: ImageCropFocus): Promise<File> {
  if (!file.type.startsWith("image/")) return file;

  const image = new Image();
  const objectUrl = URL.createObjectURL(file);

  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Görsel okunamadı."));
      image.src = objectUrl;
    });

    const targetAspect = 16 / 9;
    const sourceAspect = image.width / image.height;
    const cropWidth =
      sourceAspect > targetAspect ? Math.round(image.height * targetAspect) : image.width;
    const cropHeight =
      sourceAspect > targetAspect ? image.height : Math.round(image.width / targetAspect);

    const focusX = Math.min(1, Math.max(0, focus?.x ?? 0.5)) * image.width;
    const focusY = Math.min(1, Math.max(0, focus?.y ?? 0.5)) * image.height;
    const sourceX = Math.round(
      Math.min(Math.max(0, focusX - cropWidth / 2), image.width - cropWidth)
    );
    const sourceY = Math.round(
      Math.min(Math.max(0, focusY - cropHeight / 2), image.height - cropHeight)
    );

    const maxWidth = 900;
    const width = Math.min(maxWidth, cropWidth);
    const height = Math.round(width / targetAspect);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) throw new Error("Görsel dönüştürme başlatılamadı.");

    context.drawImage(
      image,
      sourceX,
      sourceY,
      cropWidth,
      cropHeight,
      0,
      0,
      width,
      height
    );

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", 0.82)
    );

    if (!blob) throw new Error("Görsel WebP formatına dönüştürülemedi.");
    return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.webp`, {
      type: "image/webp",
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function uploadMenuImage(
  file: File,
  focus?: ImageCropFocus
): Promise<{ imageUrl: string; imagePath: string }> {
  const resized = await resizeImage(file, focus);
  const fileName = `${crypto.randomUUID()}.webp`;
  const imagePath = `products/${fileName}`;

  const { error } = await supabase.storage
    .from(IMAGE_BUCKET)
    .upload(imagePath, resized, {
      cacheControl: "31536000",
      contentType: resized.type || "image/webp",
      upsert: false,
    });

  if (error) throw error;

  const { data } = supabase.storage.from(IMAGE_BUCKET).getPublicUrl(imagePath);
  return { imageUrl: data.publicUrl, imagePath };
}

export async function removeMenuImage(imagePath?: string): Promise<void> {
  if (!imagePath) return;
  const { error } = await supabase.storage.from(IMAGE_BUCKET).remove([imagePath]);
  if (error) throw error;
}
